import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import { ThemeProvider, createTheme } from "@mui/material";
import { toast } from "react-toastify";
import api from "../../config/URL";
import fetchAllStudentListByCenter from "../List/StudentListByCenter";

/* ================= DEBOUNCE HOOK ================= */
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};
/* ================================================ */

/* ================= CSV EXPORT ================= */
const exportVisibleColumnsToCSV = (
  table,
  rows,
  fileName = "Forfeit_Report.csv"
) => {
  const exportableColumns = table
    .getAllLeafColumns()
    .filter((col) => col.getIsVisible() && col.columnDef.accessorKey);

  const headers = exportableColumns.map((col) => col.columnDef.header);

  const csvRows = rows.map((row) =>
    exportableColumns.map((col) => `"${row[col.columnDef.accessorKey] ?? ""}"`)
  );

  const csvContent = [
    headers.join(","),
    ...csvRows.map((r) => r.join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};
/* ============================================== */

const ForfeitReport = ({ selectedCenter }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [studentData, setStudentData] = useState(null);

  /* ---------- FILTERS (BACKEND SUPPORTED) ---------- */
  const [filters, setFilters] = useState({
    studentId: "",
    courseName: "",
    invoiceNo: "",
  });

  const debouncedFilters = useDebounce(filters);

  /* ---------- PAGINATION ---------- */
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [rowCount, setRowCount] = useState(0);

  /* ---------- HANDLERS ---------- */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setFilters({
      studentId: "",
      courseName: "",
      invoiceNo: "",
    });
  };

  /* ================= TABLE COLUMNS ================= */
  const columns = useMemo(
    () => [
      {
        accessorFn: (_, index) =>
          pagination.pageIndex * pagination.pageSize + index + 1,
        header: "S.NO",
        size: 50,
      },
      { accessorKey: "centerName", header: "Centre" },
      { accessorKey: "forfeitNo", header: "Forfeit No" },
      { accessorKey: "name", header: "Student Name" },
      { accessorKey: "courseName", header: "Course" },
      { accessorKey: "invoiceNo", header: "Invoice No" },
      { accessorKey: "receiptNo", header: "Receipt No" },
      {
        accessorKey: "lastLessonDate",
        header: "Last Lesson Date",
        Cell: ({ cell }) =>
          cell.getValue()
            ? new Date(cell.getValue()).toLocaleDateString("en-GB")
            : "--",
      },
      {
        accessorKey: "withdrawDate",
        header: "Withdraw Date",
        Cell: ({ cell }) =>
          cell.getValue()
            ? new Date(cell.getValue()).toLocaleDateString("en-GB")
            : "--",
      },
      { accessorKey: "forfeitAmount", header: "Forfeit Amount" },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      { accessorKey: "updatedBy", header: "Updated By" },
    ],
    [pagination]
  );

  /* ================= THEME ================= */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: "#e6edf7",
            fontSize: "13px",
            textAlign: "center",
          },
        },
      },
    },
  });

  /* ================= API ================= */
  const getForfeitReport = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: pagination.pageIndex + 1,
        size: pagination.pageSize,
      });

      if (selectedCenter) params.append("centerId", selectedCenter);
      if (debouncedFilters.studentId)
        params.append("studentId", debouncedFilters.studentId);
      if (debouncedFilters.courseName)
        params.append("courseName", debouncedFilters.courseName);
      if (debouncedFilters.invoiceNo)
        params.append("invoiceNo", debouncedFilters.invoiceNo);

      const response = await api.get(`/getForfeitList?${params.toString()}`);

      setData(response.data?.content || []);
      setRowCount(response.data?.totalElements || 0);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch forfeit report");
    } finally {
      setLoading(false);
    }
  };

  const getStudentList = async (centerId) => {
    try {
      const student = await fetchAllStudentListByCenter(centerId);
      setStudentData(student || []);
    } catch (error) {
      toast.error("Failed to fetch student list");
      console.error(error);
    }
  };

  /* ================= EFFECTS ================= */
  useEffect(() => {
    if (selectedCenter) {
      getForfeitReport();
      getStudentList(selectedCenter);
    }
  }, [
    selectedCenter,
    pagination.pageIndex,
    pagination.pageSize,
    debouncedFilters,
  ]);

  /* ================= UI ================= */
  return (
    <div className="container-fluid my-4 px-2">
      <ol className="breadcrumb my-3">
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>{" "}
          &gt;
        </li>
        <li>Report Management &gt;</li>
        <li className="active">Forfeit Report</li>
      </ol>

      <div className="card">
        <div className="p-2 text-muted" style={{ background: "#f5f7f9" }}>
          Forfeit & Withdrawal Report
        </div>

        {/* FILTERS */}
        <div className="container-fluid px-2 py-3">
          <div className="row g-2">
            <div className="col-12 col-md-3">
              <select
                className="form-select form-select-sm"
                name="studentId"
                value={filters.studentId}
                onChange={handleFilterChange}
              >
                <option value="">Select Students</option>
                {studentData?.map((std) => (
                  <option key={std.id} value={std.id}>
                    {std.studentNames}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-4">
              <input
                className="form-control form-control-sm"
                placeholder="Course Name"
                name="courseName"
                value={filters.courseName}
                onChange={handleFilterChange}
              />
            </div>

            <div className="col-12 col-md-3">
              <input
                className="form-control form-control-sm"
                placeholder="Invoice No"
                name="invoiceNo"
                value={filters.invoiceNo}
                onChange={handleFilterChange}
              />
            </div>

            <div className="col-12 col-md-2 d-grid">
              <button className="btn btn-sm btn-border" onClick={clearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>

        <ThemeProvider theme={theme}>
          <MaterialReactTable
            columns={columns}
            data={data}
            manualPagination
            rowCount={rowCount}
            state={{ isLoading: loading, pagination }}
            onPaginationChange={setPagination}
            enableColumnActions={false}
            enableColumnFilters={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            renderTopToolbarCustomActions={({ table }) => (
              <button
                className="btn btn-sm btn-border"
                onClick={() =>
                  exportVisibleColumnsToCSV(table, data, "Forfeit_Report.csv")
                }
              >
                Export CSV
              </button>
            )}
          />
        </ThemeProvider>
      </div>
    </div>
  );
};

export default ForfeitReport;
